import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

interface CategoryLink {
  nameKey: string;
  url: string;
  icon: string;
}

const categories: CategoryLink[] = [
  { nameKey: "cat.shoes", url: "https://kaspreadsheet.com/category-911-Shoes.html", icon: "ri-footprint-line" },
  { nameKey: "cat.hoodies", url: "https://kaspreadsheet.com/search-912/name/Shoes.html", icon: "ri-t-shirt-line" },
  { nameKey: "cat.tshirts", url: "https://kaspreadsheet.com/search-913/name/Shoes.html", icon: "ri-shirt-line" },
  { nameKey: "cat.jackets", url: "https://kaspreadsheet.com/search-914/name/Shoes.html", icon: "ri-archive-line" },
  { nameKey: "cat.pants", url: "https://kaspreadsheet.com/search-915/name/Shoes.html", icon: "ri-pantone-line" },
  { nameKey: "cat.headwear", url: "https://kaspreadsheet.com/search-916/name/Shoes.html", icon: "ri-sun-line" },
  { nameKey: "cat.sets", url: "https://kaspreadsheet.com/search-917/name/Shoes.html", icon: "ri-stack-line" },
  { nameKey: "cat.underwear", url: "https://kaspreadsheet.com/search-918/name/Shoes.html", icon: "ri-checkbox-blank-circle-line" },
  { nameKey: "cat.jersey", url: "https://kaspreadsheet.com/search-919/name/Shoes.html", icon: "ri-trophy-line" },
  { nameKey: "cat.accessories", url: "https://kaspreadsheet.com/search-920/name/Shoes.html", icon: "ri-glasses-line" },
  { nameKey: "cat.bags", url: "https://kaspreadsheet.com/search-921/name/Shoes.html", icon: "ri-briefcase-line" },
  { nameKey: "cat.belts", url: "https://kaspreadsheet.com/search-922/name/Shoes.html", icon: "ri-link" },
  { nameKey: "cat.perfumes", url: "https://kaspreadsheet.com/search-923/name/Shoes.html", icon: "ri-drop-line" },
  { nameKey: "cat.other", url: "https://kaspreadsheet.com/search-924/name/Shoes.html", icon: "ri-more-line" },
];

export default function Navbar({ transparent = false }: { transparent?: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileCatOpen(false);
  }, [location.pathname]);

  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsDropdownOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => setIsDropdownOpen(false), 200);
  }, []);

  const isHomeHero = transparent && !isScrolled;

  const navClasses = `fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
    isHomeHero
      ? "bg-black/20 backdrop-blur-sm"
      : "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
  }`;

  const textClasses = isHomeHero ? "text-white" : "text-foreground-900";
  const subtleText = isHomeHero ? "text-white/70" : "text-foreground-500";

  return (
    <nav className={navClasses}>
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-17">
          {/* Logo */}
          <Link
            to="/"
            className={`font-heading text-lg md:text-2xl font-bold tracking-tight whitespace-nowrap transition-colors ${textClasses}`}
          >
            Hoobuy Forum
          </Link>

          {/* Desktop Nav */}
          <ul className="hidden md:flex items-center gap-6">
            <li>
              <Link
                to="/"
                className={`text-sm font-medium transition-colors hover:opacity-70 whitespace-nowrap ${textClasses}`}
              >
                {t("nav.home")}
              </Link>
            </li>
            <li
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium transition-colors hover:opacity-70 whitespace-nowrap cursor-pointer ${textClasses}`}
              >
                {t("nav.categories")}
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {isDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-3 min-w-[240px] animate-[fadeIn_0.15s_ease-out] max-h-[500px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.nameKey}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors whitespace-nowrap"
                    >
                      <span className="w-7 h-7 rounded-md bg-background-200/60 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-xs text-foreground-500`}></i>
                      </span>
                      {t(cat.nameKey)}
                    </a>
                  ))}
                </div>
              )}
            </li>
            <li>
              <Link
                to="/guia-de-compras"
                className={`text-sm font-medium transition-colors hover:opacity-70 whitespace-nowrap ${textClasses}`}
              >
                {t("nav.guide")}
              </Link>
            </li>
            <li>
              <Link
                to="/guia-hoobuy-sheet"
                className={`text-sm font-medium transition-colors hover:opacity-70 whitespace-nowrap ${textClasses}`}
              >
                {t("nav.blog")}
              </Link>
            </li>
          </ul>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* CTA pills - desktop */}
            <div className="hidden md:flex items-center gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer bg-accent-500 text-white hover:bg-accent-600"
              >
                {t("nav.hoobuysheet")}
                <i className="ri-external-link-line text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer bg-foreground-950 text-white hover:bg-foreground-800"
              >
                {t("nav.buynow")}
                <i className="ri-shopping-cart-line text-xs"></i>
              </a>
            </div>

            {/* Mobile menu toggle */}
            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg transition-colors ${
                isHomeHero ? "hover:bg-white/10" : "hover:bg-background-100"
              } ${textClasses}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Menu"
            >
              <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/60 pb-5 animate-[fadeIn_0.2s_ease-out] max-h-[80vh] overflow-y-auto">
            {/* Primary links */}
            <div className="pt-3 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 py-3 px-3 text-sm text-foreground-800 font-medium rounded-lg hover:bg-background-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-lg bg-background-200/60 flex items-center justify-center">
                  <i className="ri-home-line text-foreground-600"></i>
                </span>
                {t("nav.home")}
              </Link>

              {/* Mobile categories accordion */}
              <div>
                <button
                  className="flex items-center justify-between w-full py-3 px-3 text-sm text-foreground-800 font-medium rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                >
                  <span className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-background-200/60 flex items-center justify-center">
                      <i className="ri-grid-line text-foreground-600"></i>
                    </span>
                    {t("nav.categories")}
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileCatOpen ? "rotate-180" : ""}`}></i>
                </button>
                {isMobileCatOpen && (
                  <div className="ml-5 pl-3 border-l-2 border-background-200/60 space-y-0.5 mt-1 mb-2 animate-[fadeIn_0.15s_ease-out]">
                    {categories.map((cat) => (
                      <a
                        key={cat.nameKey}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 py-2.5 px-2 text-sm text-foreground-600 hover:text-accent-800 hover:bg-accent-50 rounded-lg transition-colors"
                      >
                        <span className="w-6 h-6 rounded bg-background-200/60 flex items-center justify-center flex-shrink-0">
                          <i className={`${cat.icon} text-xs text-foreground-500`}></i>
                        </span>
                        {t(cat.nameKey)}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/guia-de-compras"
                className="flex items-center gap-3 py-3 px-3 text-sm text-foreground-800 font-medium rounded-lg hover:bg-background-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-lg bg-background-200/60 flex items-center justify-center">
                  <i className="ri-book-open-line text-foreground-600"></i>
                </span>
                {t("nav.guide")}
              </Link>

              <Link
                to="/guia-hoobuy-sheet"
                className="flex items-center gap-3 py-3 px-3 text-sm text-foreground-800 font-medium rounded-lg hover:bg-background-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="w-8 h-8 rounded-lg bg-background-200/60 flex items-center justify-center">
                  <i className="ri-article-line text-foreground-600"></i>
                </span>
                {t("nav.blog")}
              </Link>
            </div>

            {/* Mobile CTAs */}
            <div className="mt-4 pt-4 border-t border-background-200/60 flex flex-col gap-2 px-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-accent-500 text-white whitespace-nowrap cursor-pointer"
              >
                {t("nav.hoobuysheet")}
                <i className="ri-external-link-line text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-foreground-950 text-white whitespace-nowrap cursor-pointer"
              >
                {t("nav.buynow")}
                <i className="ri-shopping-cart-line text-xs"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}